import { SIPToDispatcher } from "../services";
import { store } from "../redux/store";

import type JsSIP from "jssip";

/**
 * Softphone registration after its WSS to Asterisk dropped
 * (Node-taxi-server docs/SIP_LINE_LIVENESS_PLAN.md, 5.1).
 *
 * Asterisk gives the calls to the client that registered last. A laptop that
 * slept may wake up after the line went to another dispatcher; if JsSIP
 * re-registered on its own it would push that dispatcher's registration out.
 * So on `disconnected` the UA's auto-register is switched off
 * (`ua.unregister()`), the call in progress is left alone, and the UA
 * registers again only after the server confirms the line is still ours.
 *
 * Relies on JsSIP 3.10.1 (pinned in package.json): `disconnected` is emitted
 * before the registrator drops its state and before the reconnect timer
 * (>= 2 s); after reconnecting the UA registers only if `register()` was
 * called last, not `unregister()`.
 */

type UA = JsSIP.UA;

const RETRY_MS = 1_000;
const RETRY_MAX_MS = 15_000;
const WAIT_MS = 200;

/** UA whose registration waits for the line to be confirmed. */
let heldUa: UA | null = null;
/** UA with a confirmed line that registers once its WSS is up. */
let registerOnConnectUa: UA | null = null;
/** UA being stopped on purpose: its own `disconnected` is not a drop. */
let stoppingUa: UA | null = null;
let generation = 0;

const sleep = (ms: number) =>
	new Promise((resolve) => {
		setTimeout(resolve, ms);
	});

/** SIP id (claim id) of the line this tab selected, if any. */
export function getStoredClaimSipId(): number | undefined {
	try {
		const sipData = sessionStorage.getItem("sipData");
		return sipData ? JSON.parse(sipData)?.sipToDispatcherId : undefined;
	} catch {
		return undefined;
	}
}

function isCurrentUa(ua: UA) {
	return store.getState().sipToDispatcher.uaConfig?.ua === ua;
}

/** `disconnected` of the UA: stop it from registering on its own. */
export function holdSipRegistration(ua: UA) {
	// Runs after the registrator dropped its state: nothing is sent, the
	// call in progress is not touched.
	queueMicrotask(() => {
		if (ua === stoppingUa || !isCurrentUa(ua)) return;
		ua.unregister();
		heldUa = ua;
		if (registerOnConnectUa === ua) registerOnConnectUa = null;
		generation += 1;
		console.info("[SIP] registration held until the line is confirmed");
	});
}

export function isSipRegistrationHeld(ua: UA) {
	return heldUa === ua;
}

/**
 * The line was taken or dropped: forget any held registration. `stopping`:
 * the UA about to be stopped, so its `disconnected` does not hold it again.
 */
export function dropSipRegistrationHold(stopping?: UA | null) {
	if (stopping) stoppingUa = stopping;
	heldUa = null;
	registerOnConnectUa = null;
	generation += 1;
}

/**
 * The line is confirmed ours (claim success): register the held UA now, or
 * as soon as its WSS is up. No-op when nothing is held.
 */
export function releaseSipRegistration() {
	const ua = heldUa;
	if (!ua) return;
	heldUa = null;
	generation += 1;

	if (!isCurrentUa(ua)) return;

	if (ua.isConnected()) {
		console.info("[SIP] line confirmed, registering");
		ua.register();
	} else {
		registerOnConnectUa = ua;
	}
}

/** `connected` of the UA. */
export function onSipTransportConnected(
	ua: UA,
	onLineLost: () => Promise<void>,
) {
	if (registerOnConnectUa === ua) {
		registerOnConnectUa = null;
		if (isCurrentUa(ua)) {
			console.info("[SIP] line confirmed, registering");
			ua.register();
		}
		return;
	}

	if (heldUa === ua) confirmLine(ua, onLineLost);
}

/**
 * Asks the server whether the line is still ours, retrying until answered.
 * Without a gateway connection it stops: the reconnect reclaim in App.tsx
 * confirms the line and calls releaseSipRegistration.
 */
async function confirmLine(ua: UA, onLineLost: () => Promise<void>) {
	const claimSipId = getStoredClaimSipId();
	if (claimSipId == null) {
		dropSipRegistrationHold();
		return;
	}

	generation += 1;
	const myGeneration = generation;
	const { selectionVersion } = SIPToDispatcher;
	// Stale: a newer confirmation started, the hold was released or dropped,
	// the dispatcher picked / dropped a line, or the UA was replaced.
	const isStale = () =>
		myGeneration !== generation ||
		heldUa !== ua ||
		selectionVersion !== SIPToDispatcher.selectionVersion ||
		getStoredClaimSipId() !== claimSipId ||
		!isCurrentUa(ua);

	for (let attempt = 1; ; attempt += 1) {
		// Wait for the dispatcher's own pick / release: sending now would
		// get a higher claimSeq and override it.
		while (SIPToDispatcher.selectionInFlight) {
			// eslint-disable-next-line no-await-in-loop
			await sleep(WAIT_MS);
			if (isStale()) return;
		}
		if (isStale()) return;
		if (!store.getState().prpc.connected) return;

		// eslint-disable-next-line no-await-in-loop
		const data = await SIPToDispatcher.initSession(
			{ sipToDispatcherId: claimSipId, allowSameUserReclaim: true },
			{ reclaim: true },
		);
		if (isStale()) return;

		console.info("[SIP] line confirmation result", {
			claimSipId,
			attempt,
			success: data?.success,
			reason: data?.reason,
		});

		if (data?.success === true) {
			releaseSipRegistration();
			return;
		}

		if (
			data?.success === false &&
			(!data.reason ||
				data.reason === "busy" ||
				data.reason === "unknown_sip")
		) {
			dropSipRegistrationHold();
			// eslint-disable-next-line no-await-in-loop
			await onLineLost();
			return;
		}

		// No answer, lock_timeout or stale (a parallel request of this tab
		// won): ask again.
		// eslint-disable-next-line no-await-in-loop
		await sleep(Math.min(RETRY_MS * 2 ** (attempt - 1), RETRY_MAX_MS));
	}
}
